import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Trash,
  PencilSimple,
  ArrowsClockwise,
  Eye,
  ArrowSquareOut,
  X,
} from "@phosphor-icons/react";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const EMPTY_PAGE = {
  slug: "",
  active: true,
  order: 100,
  title: "",
  meta_title: "",
  meta_description: "",
  h1: "",
  hero_lead: "",
  hero_bullets: [""],
  seo_keywords: [],
  features: [{ title: "", body: "" }],
  use_cases: [{ role: "", text: "" }],
  faq: [{ q: "", a: "" }],
  cta: { primary: "Prova gratis i 30 dagar", secondary: "Boka demo på 15 min" },
  schema_org_article: { headline: "", description: "" },
  marketing: {
    hooks: [],
    personas: [],
    competitors: [],
    utm_source: "industry-page",
    utm_medium: "organic",
    utm_campaign: "",
  },
};

/** Super Admin-vy för branschlandningssidor. Skapa/redigera/radera i MongoDB. */
export default function LandingPagesAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { originalSlug|null, data }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE}/api/landing-pages/admin/all`, {
        withCredentials: true,
      });
      setItems(r.data?.items || []);
    } catch {
      toast.error("Kunde inte hämta landningssidor");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startCreate = () => setEditing({ originalSlug: null, data: JSON.parse(JSON.stringify(EMPTY_PAGE)) });
  const startEdit = (page) =>
    setEditing({ originalSlug: page.slug, data: JSON.parse(JSON.stringify(page)) });
  const cancelEdit = () => setEditing(null);

  const remove = async (slug) => {
    if (!window.confirm(`Radera landningssidan '${slug}'? Detta går inte att ångra.`)) return;
    try {
      await axios.delete(`${API_BASE}/api/landing-pages/${slug}`, { withCredentials: true });
      toast.success("Landningssidan raderad");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte radera");
    }
  };

  const save = async () => {
    if (!editing) return;
    const { data, originalSlug } = editing;
    // Basic validation
    if (!data.slug.trim() || !data.title.trim() || !data.h1.trim()) {
      toast.error("Slug, titel och H1 måste fyllas i");
      return;
    }
    // Trim lists
    const cleaned = {
      ...data,
      hero_bullets: data.hero_bullets.filter((b) => b.trim()),
      seo_keywords: data.seo_keywords.filter((k) => k.trim()),
      features: data.features.filter((f) => f.title.trim() || f.body.trim()),
      use_cases: data.use_cases.filter((u) => u.role.trim() || u.text.trim()),
      faq: data.faq.filter((f) => f.q.trim() || f.a.trim()),
      marketing: {
        ...data.marketing,
        hooks: (data.marketing?.hooks || []).filter((x) => x.trim()),
        personas: (data.marketing?.personas || []).filter((x) => x.trim()),
        competitors: (data.marketing?.competitors || []).filter((x) => x.trim()),
      },
    };
    try {
      if (originalSlug) {
        await axios.put(`${API_BASE}/api/landing-pages/${originalSlug}`, cleaned, {
          withCredentials: true,
        });
        toast.success("Sparad");
      } else {
        await axios.post(`${API_BASE}/api/landing-pages`, cleaned, { withCredentials: true });
        toast.success("Landningssida skapad");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte spara");
    }
  };

  const seedDefaults = async () => {
    try {
      const r = await axios.post(
        `${API_BASE}/api/landing-pages/seed`,
        {},
        { withCredentials: true },
      );
      toast.success(`Seed klar: ${r.data.created} skapade, ${r.data.skipped} hoppade`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte seed:a");
    }
  };

  return (
    <div data-testid="admin-landing-pages-panel">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="font-heading text-xl mb-1">Landningssidor per bransch</h2>
          <p className="text-sm text-text-muted leading-relaxed max-w-2xl">
            Lägg till nya branscher (t.ex. VVS, El, Måleri) utan kodrelease. Varje sida
            får en URL <code className="font-mono text-xs bg-app px-1 py-0.5 rounded">/tidrapportering-&lt;slug&gt;</code>{" "}
            och skrivs automatiskt in i <code className="font-mono text-xs bg-app px-1 py-0.5 rounded">/api/sitemap.xml</code>.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            className="border border-app hover:bg-surface-hover rounded-md px-3 py-2 text-sm inline-flex items-center gap-2"
            data-testid="landing-pages-refresh"
          >
            <ArrowsClockwise size={14} weight="bold" />
            Uppdatera
          </button>
          <button
            onClick={seedDefaults}
            className="border border-app hover:bg-surface-hover rounded-md px-3 py-2 text-sm inline-flex items-center gap-2"
            data-testid="landing-pages-seed"
            title="Skapar bygg + snickeri om de saknas"
          >
            Seed grundpaket
          </button>
          <button
            onClick={startCreate}
            className="bg-brand text-white hover:bg-brand-hover rounded-md px-3 py-2 text-sm inline-flex items-center gap-2"
            data-testid="landing-pages-new"
          >
            <Plus size={14} weight="bold" />
            Ny landningssida
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-text-muted py-8 text-center">Laddar…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-text-muted py-8 text-center border border-dashed border-app rounded-lg">
          Inga landningssidor än. Klicka <strong>Seed grundpaket</strong> eller{" "}
          <strong>Ny landningssida</strong> för att komma igång.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="landing-pages-list">
          {items.map((p) => (
            <li
              key={p.slug}
              className="bg-surface border border-app rounded-lg p-4 flex flex-wrap items-center justify-between gap-3"
              data-testid={`landing-page-row-${p.slug}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-heading text-base truncate">{p.title}</span>
                  <span
                    className={
                      "text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full " +
                      (p.active
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-neutral-200 text-neutral-600")
                    }
                  >
                    {p.active ? "Aktiv" : "Inaktiv"}
                  </span>
                  <span className="text-xs text-text-muted">Ordning: {p.order}</span>
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-muted mt-1">
                  <code className="font-mono text-xs bg-app px-1.5 py-0.5 rounded">/tidrapportering-{p.slug}</code>
                  <span>{p.features?.length || 0} features</span>
                  <span>{p.faq?.length || 0} FAQ</span>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <a
                  href={`/tidrapportering-${p.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border border-app hover:bg-surface-hover rounded-md p-2"
                  title="Öppna publik sida"
                  data-testid={`landing-page-open-${p.slug}`}
                >
                  <ArrowSquareOut size={14} />
                </a>
                <button
                  onClick={() => startEdit(p)}
                  className="border border-app hover:bg-surface-hover rounded-md p-2"
                  title="Redigera"
                  data-testid={`landing-page-edit-${p.slug}`}
                >
                  <PencilSimple size={14} />
                </button>
                <button
                  onClick={() => remove(p.slug)}
                  className="border border-app hover:bg-red-50 text-red-600 rounded-md p-2"
                  title="Radera"
                  data-testid={`landing-page-delete-${p.slug}`}
                >
                  <Trash size={14} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <PageEditor
          state={editing}
          setState={setEditing}
          onSave={save}
          onCancel={cancelEdit}
        />
      )}
    </div>
  );
}

// ---------------- Editor Modal ----------------

function PageEditor({ state, setState, onSave, onCancel }) {
  const { data, originalSlug } = state;
  const update = (patch) =>
    setState({ ...state, data: { ...data, ...patch } });
  const updateNested = (key, patch) =>
    setState({ ...state, data: { ...data, [key]: { ...(data[key] || {}), ...patch } } });

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-start md:items-center justify-center p-4 overflow-y-auto"
      data-testid="landing-page-editor"
    >
      <div className="bg-app border border-app rounded-xl w-full max-w-3xl my-8">
        <div className="sticky top-0 bg-app border-b border-app px-6 py-4 flex items-center justify-between rounded-t-xl">
          <h3 className="font-heading text-xl">
            {originalSlug ? `Redigera: ${originalSlug}` : "Ny landningssida"}
          </h3>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-surface rounded-md"
            data-testid="landing-page-editor-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Grunduppgifter */}
          <Section title="Grunduppgifter">
            <Row>
              <Field
                label="Slug (URL-del)"
                required
                placeholder="vvs"
                value={data.slug}
                onChange={(v) => update({ slug: v.toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
                testid="lp-slug"
              />
              <Field
                label="Sorteringsordning"
                type="number"
                value={data.order}
                onChange={(v) => update({ order: parseInt(v || "0", 10) })}
                testid="lp-order"
              />
              <label className="flex items-center gap-2 text-sm mt-6">
                <input
                  type="checkbox"
                  checked={data.active}
                  onChange={(e) => update({ active: e.target.checked })}
                  data-testid="lp-active"
                />
                Aktiv (synlig publikt)
              </label>
            </Row>
            <Field
              label="Titel (intern)"
              required
              value={data.title}
              onChange={(v) => update({ title: v })}
              testid="lp-title"
            />
            <Field
              label="Meta Title (Google-flik)"
              value={data.meta_title}
              onChange={(v) => update({ meta_title: v })}
              testid="lp-meta-title"
            />
            <Field
              label="Meta Description (Google-snippet, max 160 tecken)"
              value={data.meta_description}
              onChange={(v) => update({ meta_description: v })}
              multiline
              testid="lp-meta-desc"
            />
          </Section>

          {/* Hero */}
          <Section title="Hero">
            <Field label="H1" required value={data.h1} onChange={(v) => update({ h1: v })} testid="lp-h1" />
            <Field
              label="Hero-lead (ingress under H1)"
              value={data.hero_lead}
              onChange={(v) => update({ hero_lead: v })}
              multiline
              testid="lp-hero-lead"
            />
            <ListEditor
              label="Hero-bullets (kryssmarkerad lista)"
              items={data.hero_bullets}
              onChange={(items) => update({ hero_bullets: items })}
              placeholder="Ex: Stämpling med objektsval från mobilen"
              testid="lp-hero-bullets"
            />
          </Section>

          {/* Features */}
          <Section title="Features (3 kort per rad)">
            <ObjectListEditor
              items={data.features}
              schema={[
                { key: "title", label: "Titel", placeholder: "Personalliggare som gratis tillval" },
                { key: "body", label: "Beskrivning", multiline: true },
              ]}
              onChange={(items) => update({ features: items })}
              addLabel="Lägg till feature"
              testid="lp-features"
            />
          </Section>

          {/* Use cases */}
          <Section title="Use cases (2 kort per rad)">
            <ObjectListEditor
              items={data.use_cases}
              schema={[
                { key: "role", label: "Roll", placeholder: "Platschef" },
                { key: "text", label: "Beskrivning", multiline: true },
              ]}
              onChange={(items) => update({ use_cases: items })}
              addLabel="Lägg till use case"
              testid="lp-usecases"
            />
          </Section>

          {/* FAQ */}
          <Section title="FAQ (Google FAQ-schema aktiveras automatiskt)">
            <ObjectListEditor
              items={data.faq}
              schema={[
                { key: "q", label: "Fråga" },
                { key: "a", label: "Svar", multiline: true },
              ]}
              onChange={(items) => update({ faq: items })}
              addLabel="Lägg till fråga"
              testid="lp-faq"
            />
          </Section>

          {/* CTA */}
          <Section title="Call-to-action-knappar">
            <Row>
              <Field
                label="Primär CTA"
                value={data.cta.primary}
                onChange={(v) => updateNested("cta", { primary: v })}
                testid="lp-cta-primary"
              />
              <Field
                label="Sekundär CTA"
                value={data.cta.secondary}
                onChange={(v) => updateNested("cta", { secondary: v })}
                testid="lp-cta-secondary"
              />
            </Row>
          </Section>

          {/* Schema.org */}
          <Section title="Schema.org (SEO article-block)">
            <Field
              label="Headline"
              value={data.schema_org_article.headline}
              onChange={(v) => updateNested("schema_org_article", { headline: v })}
              testid="lp-schema-headline"
            />
            <Field
              label="Description"
              value={data.schema_org_article.description}
              onChange={(v) => updateNested("schema_org_article", { description: v })}
              multiline
              testid="lp-schema-desc"
            />
          </Section>

          {/* SEO Keywords */}
          <Section title="SEO-nyckelord (används av framtida marketing-bot)">
            <ListEditor
              label="Nyckelord"
              items={data.seo_keywords}
              onChange={(items) => update({ seo_keywords: items })}
              placeholder="tidrapport vvs"
              testid="lp-seo-keywords"
            />
          </Section>
        </div>

        <div className="sticky bottom-0 bg-app border-t border-app px-6 py-4 flex items-center justify-end gap-2 rounded-b-xl">
          <button
            onClick={onCancel}
            className="border border-app hover:bg-surface-hover rounded-md px-4 py-2 text-sm"
            data-testid="landing-page-editor-cancel"
          >
            Avbryt
          </button>
          <button
            onClick={onSave}
            className="bg-brand text-white hover:bg-brand-hover rounded-md px-4 py-2 text-sm font-medium"
            data-testid="landing-page-editor-save"
          >
            Spara
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------- Editor byggblock ----------------

function Section({ title, children }) {
  return (
    <div>
      <h4 className="font-heading text-sm uppercase tracking-widest text-text-muted mb-3">
        {title}
      </h4>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>;
}

function Field({ label, value, onChange, multiline, placeholder, required, testid, type = "text" }) {
  return (
    <div>
      <label className="block text-xs text-text-muted mb-1">
        {label}
        {required && <span className="text-red-600 ml-0.5">*</span>}
      </label>
      {multiline ? (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
          className="w-full border border-app bg-app rounded-md px-3 py-2 text-sm"
          data-testid={testid}
        />
      ) : (
        <input
          type={type}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full border border-app bg-app rounded-md px-3 py-2 text-sm"
          data-testid={testid}
        />
      )}
    </div>
  );
}

function ListEditor({ label, items, onChange, placeholder, testid }) {
  const update = (i, v) => {
    const next = [...items];
    next[i] = v;
    onChange(next);
  };
  const add = () => onChange([...(items || []), ""]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <div>
      {label && <div className="text-xs text-text-muted mb-1">{label}</div>}
      <div className="space-y-2" data-testid={testid}>
        {(items || []).map((v, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              value={v}
              onChange={(e) => update(i, e.target.value)}
              placeholder={placeholder}
              className="flex-1 border border-app bg-app rounded-md px-3 py-2 text-sm"
            />
            <button
              onClick={() => remove(i)}
              className="p-2 border border-app rounded-md hover:bg-red-50 text-red-600"
              title="Ta bort"
            >
              <Trash size={14} />
            </button>
          </div>
        ))}
        <button
          onClick={add}
          className="text-xs text-brand hover:underline inline-flex items-center gap-1"
        >
          <Plus size={12} /> Lägg till rad
        </button>
      </div>
    </div>
  );
}

function ObjectListEditor({ items, schema, onChange, addLabel, testid }) {
  const update = (i, key, v) => {
    const next = [...items];
    next[i] = { ...next[i], [key]: v };
    onChange(next);
  };
  const add = () => {
    const empty = {};
    schema.forEach((s) => (empty[s.key] = ""));
    onChange([...(items || []), empty]);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-3" data-testid={testid}>
      {(items || []).map((it, i) => (
        <div key={i} className="border border-app rounded-md p-3 relative space-y-2">
          <button
            onClick={() => remove(i)}
            className="absolute top-2 right-2 text-red-600 hover:bg-red-50 rounded p-1"
            title="Ta bort"
          >
            <Trash size={12} />
          </button>
          {schema.map((s) => (
            <Field
              key={s.key}
              label={s.label}
              placeholder={s.placeholder}
              multiline={s.multiline}
              value={it[s.key]}
              onChange={(v) => update(i, s.key, v)}
            />
          ))}
        </div>
      ))}
      <button
        onClick={add}
        className="text-xs text-brand hover:underline inline-flex items-center gap-1"
      >
        <Plus size={12} /> {addLabel || "Lägg till"}
      </button>
    </div>
  );
}
